import React, { useMemo, useState } from "react";
import axios from "axios";

import { GlobalState } from "../../../context/GlobalContext";
import { AuthState } from "context/AuthContext";

import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { grey } from "@mui/material/colors";

import { API, channelData, seasonOptions, brandList, getAllowedBpBrands, getAllowedBpChannels } from "../data";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";

const normalizeOpenDate = (value) => {
	if (!value) return null;
	if (typeof value === "object" && value.value != null) value = value.value;
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		return value.toISOString().slice(0, 10);
	}
	if (typeof value === "string") {
		const trimmed = value.trim();
		if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
		const parsed = new Date(trimmed);
		if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
		return null;
	}
	const parsed = new Date(value);
	if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
	return null;
};

const modalStyle = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 460,
	bgcolor: "background.paper",
	borderRadius: 3,
	boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)",
	border: `1px solid ${grey[200]}`,
	p: 4,
};

const CreatePlanModal = ({ open, onClose, onCreated }) => {
	const { setLoadingOpen, setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const { user } = AuthState();
	const currentYear = new Date().getFullYear();

	const [season, setSeason] = useState("");
	const [year, setYear] = useState(currentYear);
	const [channel, setChannel] = useState("");
	const [brand, setBrand] = useState("");

	const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear + i);

	const allowedBrands = useMemo(() => getAllowedBpBrands(user?.role), [user?.role]);
	const allowedChannels = useMemo(() => getAllowedBpChannels(user?.role), [user?.role]);
	const brandOptions =
		allowedBrands === null ? brandList : brandList.filter((b) => allowedBrands.includes(b.key));
	const channelOptions =
		allowedChannels === null
			? channelData
			: channelData.filter((c) => allowedChannels.includes(c.key));

	const showSnack = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({ ...snackBar, message, severity });
	};

	const handleCreate = async () => {
		if (!season || !year || !channel || !brand) {
			showSnack("Please fill all fields");
			return;
		}

		try {
			setLoadingOpen(true);
			const name = `${season}-${String(year).slice(-2)}`;

			const storeRes = await axios.get(API.GET_STORE_MASTER, {
				params: { channel, brand, season, year },
			});
			const stores = (storeRes.data?.result || []).filter((s) => s.mapping_code);

			if (!stores.length) {
				showSnack("No operational stores with mapping_code found for selected channel/brand");
				return;
			}

			const bpStores = stores.map((s) => ({
				mapping_code: s.mapping_code,
				store_code: s.store_code || s.mapping_code,
				store_name: s.store_name,
				brand: brand,
				channel: s.channel || channel,
				city: s.city,
				city_tier: s.city_tier,
				state: s.state,
				state_zone: s.state_zone,
				store_grade: s.store_grade,
				store_type: s.store_type,
				area: s.area,
				pin: s.pin,
				open_date: normalizeOpenDate(s.open_date),
				status: s.status || "OPERATIONAL",
				rsm_email_id: s.rsm_email_id,
				rsm_name: s.rsm_name,
				bh_email_id: s.bh_email_id,
				bh_name: s.bh_name,
			}));

			const { data } = await axios.post(API.CREATE_PLAN, {
				name,
				season,
				year,
				channel,
				brand,
				total_count: bpStores.length,
				bpStores,
			});

			if (data.status === "success") {
				showSnack("Plan created successfully", "success");
				onCreated?.();
				onClose();
			}
		} catch (err) {
			console.log(err);
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingOpen(false);
		}
	};

	return (
		<Modal open={open} onClose={onClose}>
			<Box sx={modalStyle}>
				<Typography variant="h6" fontWeight={700} mb={3}>Create Business Plan</Typography>
				<Stack spacing={2.5}>
					<FormControl size="small" fullWidth>
						<InputLabel>Season</InputLabel>
						<Select value={season} label="Season" onChange={(e) => setSeason(e.target.value)}>
							{seasonOptions.map((s) => (
								<MenuItem key={s.key} value={s.key}>{s.name}</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" fullWidth>
						<InputLabel>Year</InputLabel>
						<Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
							{yearOptions.map((y) => (
								<MenuItem key={y} value={y}>{y}</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" fullWidth>
						<InputLabel>Channel</InputLabel>
						<Select value={channel} label="Channel" onChange={(e) => setChannel(e.target.value)}>
							{channelOptions.map((c) => (
								<MenuItem key={c.key} value={c.key}>{c.name}</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" fullWidth>
						<InputLabel>Brand</InputLabel>
						<Select value={brand} label="Brand" onChange={(e) => setBrand(e.target.value)}>
							{brandOptions.map((b) => (
								<MenuItem key={b.key} value={b.key}>{b.name}</MenuItem>
							))}
						</Select>
					</FormControl>

					<Stack direction="row" spacing={2} justifyContent="flex-end">
						<Button variant="outlined" onClick={onClose}>Cancel</Button>
						<Button variant="contained" onClick={handleCreate}>Create</Button>
					</Stack>
				</Stack>
			</Box>
		</Modal>
	);
};

export default CreatePlanModal;
